/* XArchive product interactions. Custom elements reconnect in the Shopify editor. */
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
  const wrap = (value, length) => ((value % length) + length) % length;
  const motion = () => !reduced.matches && document.body.dataset.productMotion !== 'false';
  document.addEventListener('pointerdown', event => {
    if (!motion() || event.button !== 0 || !event.target.closest('button:not(:disabled), a, summary')) return;
    const pulse = document.createElement('span');
    pulse.className = 'xa-tap-feedback';
    pulse.setAttribute('aria-hidden', 'true');
    pulse.style.left = event.clientX + 'px'; pulse.style.top = event.clientY + 'px';
    document.body.append(pulse);
    setTimeout(() => pulse.remove(), 500);
  }, { passive: true });

  class ArchiveElement extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      queueMicrotask(() => { if (this.isConnected && this.controller) this.mount(); });
    }
    on(target, event, handler, options = {}) {
      target?.addEventListener(event, handler, { ...options, signal: this.controller.signal });
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      clearInterval(this.timer);
      cancelAnimationFrame(this.frame);
    }
  }

  class ProductCard extends ArchiveElement {
    mount() {
      this.slides = [...this.querySelectorAll('[data-card-slide]')];
      this.position = 0;
      this.lockHover = false;
      const stage = this.querySelector('.product-card__stage');
      this.querySelectorAll('[data-card-controls], [data-card-count]').forEach(e => e.hidden = false);
      const show = (index) => {
        if (!this.slides.length) return;
        this.position = wrap(index, this.slides.length);
        this.slides.forEach((slide, i) => {
          slide.classList.toggle('is-active', i === this.position);
          slide.setAttribute('aria-hidden', String(i !== this.position));
        });
        const counter = this.querySelector('[data-card-count]');
        if (counter) counter.textContent = (this.position + 1) + ' / ' + this.slides.length;
      };
      show(0);
      this.on(this.querySelector('[data-card-prev]'), 'click', () => { this.lockHover = true; show(this.position - 1); });
      this.on(this.querySelector('[data-card-next]'), 'click', () => { this.lockHover = true; show(this.position + 1); });
      this.on(stage, 'pointerenter', () => {
        if (fine.matches && this.dataset.hoverImage === 'true' && !this.lockHover && this.slides.length > 1) show(1);
      });
      this.on(stage, 'pointerleave', () => {
        if (!this.lockHover) show(0);
        stage.style.removeProperty('--card-rx');
        stage.style.removeProperty('--card-ry');
        stage.style.removeProperty('--light-x');
        stage.style.removeProperty('--light-y');
      });
      this.on(stage, 'pointermove', event => {
        if (!motion() || !fine.matches || document.body.dataset.productTilt === 'false') return;
        const b = stage.getBoundingClientRect();
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => {
          stage.style.setProperty('--card-ry', ((event.clientX - b.left) / b.width - .5) * 5 + 'deg');
          stage.style.setProperty('--card-rx', -((event.clientY - b.top) / b.height - .5) * 5 + 'deg');
          stage.style.setProperty('--light-x', (event.clientX - b.left) / b.width * 100 + '%');
          stage.style.setProperty('--light-y', (event.clientY - b.top) / b.height * 100 + '%');
        });
      });
      let start;
      this.on(stage, 'pointerdown', e => { if (e.pointerType !== 'mouse') start = [e.clientX, e.clientY]; });
      this.on(stage, 'pointerup', e => {
        if (!start) return;
        const dx = e.clientX - start[0], dy = e.clientY - start[1];
        start = null;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) {
          this.suppressClickUntil = performance.now() + 400;
          this.lockHover = true;
          show(this.position + (dx < 0 ? 1 : -1));
        }
      });
      this.on(stage, 'pointercancel', () => { start = null; });
      this.on(stage, 'click', e => {
        if (performance.now() < (this.suppressClickUntil || 0)) { e.preventDefault(); e.stopPropagation(); }
      }, { capture: true });
    }
  }

  class ProductGallery extends ArchiveElement {
    mount() {
      this.slides = [...this.querySelectorAll('[data-gallery-slide]')];
      this.position = 0;
      this.querySelectorAll('[data-gallery-controls], [data-zoom]').forEach(e => e.hidden = false);
      this.on(this.querySelector('[data-gallery-prev]'), 'click', () => this.show(this.position - 1));
      this.on(this.querySelector('[data-gallery-next]'), 'click', () => this.show(this.position + 1));
      this.on(this, 'click', e => {
        const button = e.target.closest('[data-gallery-index]');
        if (button) this.show(Number(button.dataset.galleryIndex));
      });
      this.on(this.closest('[data-product-root]'), 'variant:media', e => {
        const index = this.slides.findIndex(s => s.dataset.mediaId === e.detail);
        if (index >= 0) this.show(index);
      });
      const stage = this.querySelector('[data-gallery-stage]');
      let start;
      this.on(stage, 'pointerdown', e => {
        if (!e.target.closest('model-viewer, video, iframe, button')) start = [e.clientX, e.clientY];
      });
      this.on(stage, 'pointerup', e => {
        if (!start) return;
        const [x, y] = start; start = null;
        if (Math.abs(e.clientX - x) > 45 && Math.abs(e.clientY - y) < Math.abs(e.clientX - x)) this.show(this.position + (e.clientX < x ? 1 : -1));
      });
      this.on(stage, 'pointercancel', () => { start = null; });
      const dialog = this.querySelector('[data-product-lightbox]');
      const enlarged = dialog?.querySelector('[data-lightbox-image]');
      this.on(this, 'click', e => {
        const zoom = e.target.closest('[data-zoom]');
        if (!zoom || !dialog) return;
        enlarged.src = zoom.dataset.zoomSrc;
        enlarged.alt = zoom.closest('[data-gallery-slide]').querySelector('img')?.alt || 'Product detail';
        dialog.showModal();
      });
      this.on(dialog?.querySelector('[data-close-lightbox]'), 'click', () => dialog.close());
      this.on(dialog, 'click', e => { if (e.target === dialog) dialog.close(); });
    }
    show(index) {
      if (!this.slides.length) return;
      this.position = wrap(index, this.slides.length);
      this.slides.forEach((slide, i) => {
        const active = i === this.position;
        slide.hidden = !active;
        slide.classList.toggle('is-active', active);
        if (!active) {
          slide.querySelectorAll('video').forEach(v => v.pause());
          // Reset external video players so hidden audio cannot keep playing.
          slide.querySelectorAll('iframe').forEach(f => { const src = f.getAttribute('src'); if (src) f.setAttribute('src', src); });
          slide.querySelector('model-viewer')?.removeAttribute('auto-rotate');
        }
      });
      this.querySelectorAll('[data-gallery-index]').forEach(b => b.setAttribute('aria-pressed', String(Number(b.dataset.galleryIndex) === this.position)));
      const count = this.querySelector('[data-gallery-count]');
      if (count) count.textContent = (this.position + 1) + ' / ' + this.slides.length;
    }
  }

  class SpinViewer extends ArchiveElement {
    mount() {
      this.frames = [...this.querySelector('template').content.querySelectorAll('[data-src]')].map(e => ({ src: e.dataset.src, alt: e.dataset.alt }));
      if (this.frames.length < 3) return;
      this.cache = new Map();
      this.position = 0;
      this.requestId = 0;
      this.image = this.querySelector('.spin-viewer__image');
      this.stage = this.querySelector('[data-spin-stage]');
      this.status = this.querySelector('[data-spin-status]');
      this.range = this.querySelector('[data-spin-range]');
      this.playButton = this.querySelector('[data-spin-play]');
      this.querySelectorAll('[data-spin-controls]').forEach(e => e.hidden = false);
      this.on(this.querySelector('[data-spin-prev]'), 'click', () => { this.stop(); this.show(this.position - 1); });
      this.on(this.querySelector('[data-spin-next]'), 'click', () => { this.stop(); this.show(this.position + 1); });
      this.on(this.querySelector('[data-spin-reset]'), 'click', () => { this.stop(); this.show(0); });
      this.on(this.playButton, 'click', () => this.timer ? this.stop() : this.play());
      this.on(this.range, 'input', () => { this.stop(); this.show(Number(this.range.value)); });
      this.on(this.stage, 'keydown', e => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
        e.preventDefault(); this.stop();
        this.show(e.key === 'Home' ? 0 : e.key === 'End' ? this.frames.length - 1 : this.position + (e.key === 'ArrowRight' ? 1 : -1));
      });
      this.on(this.stage, 'dragstart', e => e.preventDefault());
      this.on(this.stage, 'pointerdown', e => {
        if (e.button !== 0 || !e.isPrimary) return;
        this.stop();
        this.drag = { x: e.clientX, y: e.clientY, index: this.position, id: e.pointerId };
        this.stage.setPointerCapture(e.pointerId);
        this.stage.classList.add('is-dragging');
      });
      this.on(this.stage, 'pointermove', e => {
        if (!this.drag || this.drag.id !== e.pointerId) return;
        const distance = e.clientX - this.drag.x;
        const step = Math.max(this.stage.clientWidth / this.frames.length, 7);
        this.show(this.drag.index - Math.round(distance / step));
      });
      const end = () => { this.drag = null; this.stage.classList.remove('is-dragging'); };
      this.on(this.stage, 'pointerup', end);
      this.on(this.stage, 'pointercancel', end);
      this.on(this.stage, 'lostpointercapture', end);
      this.on(document, 'visibilitychange', () => { if (document.hidden) this.stop(); });
      this.on(reduced, 'change', () => { if (reduced.matches) this.stop(); });
      this.on(this.stage, 'focus', () => this.warm(), { once: true });
      this.on(this.stage, 'pointerenter', () => this.warm(), { once: true });
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          if (entries[0].isIntersecting) { this.load(0); this.load(1); }
          else this.stop();
        }, { threshold: .1 });
        this.observer.observe(this);
      }
    }
    load(index) {
      if (this.cache.has(index)) return this.cache.get(index);
      const job = new Promise(resolve => {
        const image = new Image();
        const finish = ok => { clearTimeout(timeout); resolve(ok ? image : null); };
        const timeout = setTimeout(() => finish(false), 15000);
        image.onload = () => finish(true);
        image.onerror = () => finish(false);
        image.src = this.frames[index].src;
      });
      this.cache.set(index, job);
      return job;
    }
    warm() {
      if (this.warming) return;
      this.warming = true;
      let next = 0;
      const worker = async () => {
        while (next < this.frames.length && this.isConnected) await this.load(next++);
      };
      Promise.all([worker(), worker(), worker()]);
    }
    async show(value) {
      const index = wrap(value, this.frames.length);
      this.position = index;
      const requestId = ++this.requestId;
      const loaded = await this.load(index);
      if (!this.isConnected || requestId !== this.requestId) return;
      if (!loaded) {
        this.stop();
        this.status.textContent = 'This angle could not load. Choose another angle or reload to retry.';
        return;
      }
      this.image.removeAttribute('srcset');
      this.image.src = loaded.src;
      this.image.alt = this.frames[index].alt;
      this.range.value = String(index);
      const degrees = Math.round(index * 360 / this.frames.length);
      this.range.setAttribute('aria-valuetext', degrees + ' degrees');
      this.querySelector('[data-spin-angle]').textContent = degrees + '°';
      this.status.textContent = 'Drag to explore · arrows to rotate';
      this.load(wrap(index + 1, this.frames.length));
    }
    play() {
      this.warm();
      const milliseconds = Math.max(70, Number(this.dataset.duration || 8) * 1000 / this.frames.length);
      this.timer = setInterval(() => this.show(this.position + 1), milliseconds);
      this.playButton.textContent = 'PAUSE ROTATION';
      this.playButton.setAttribute('aria-pressed', 'true');
    }
    stop() {
      clearInterval(this.timer); this.timer = null;
      if (this.playButton) { this.playButton.textContent = 'AUTO ROTATE'; this.playButton.setAttribute('aria-pressed', 'false'); }
    }
  }

  let modelLibrary;
  const waitForModelViewer = (duration = 20000) => {
    if (customElements.get('model-viewer')) return Promise.resolve();
    return new Promise((resolve, reject) => {
      let settled = false;
      const finish = callback => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        callback();
      };
      const timeout = setTimeout(() => finish(() => reject(new Error('3D viewer loading timed out'))), duration);
      customElements.whenDefined('model-viewer').then(() => finish(resolve));
    });
  };
  const loadModelLibrary = () => {
    if (customElements.get('model-viewer')) return Promise.resolve();
    if (modelLibrary) return modelLibrary;
    modelLibrary = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.type = 'module';
      script.src = 'https://cdn.jsdelivr.net/npm/@google/model-viewer@4.0.0/dist/model-viewer.min.js';
      const timeout = setTimeout(() => reject(new Error('3D viewer loading timed out')), 20000);
      script.onload = () => { clearTimeout(timeout); customElements.whenDefined('model-viewer').then(resolve); };
      script.onerror = () => { clearTimeout(timeout); reject(new Error('3D viewer could not load')); };
      document.head.append(script);
    }).catch(error => { modelLibrary = null; throw error; });
    return modelLibrary;
  };
  window.XArchiveLoadModel = loadModelLibrary;
  const initHeroModels = (root = document) => root.querySelectorAll('[data-hero-model]').forEach(model => {
    loadModelLibrary().then(() => { if (motion()) model.setAttribute('auto-rotate', ''); }).catch(() => {});
    reduced.addEventListener('change', () => { if (reduced.matches) model.removeAttribute('auto-rotate'); }, { once: true });
  });
  initHeroModels();
  document.addEventListener('shopify:section:load', event => initHeroModels(event.target));

  class ModelStage extends ArchiveElement {
    mount() {
      const button = this.querySelector('[data-load-model]');
      const status = this.querySelector('[data-model-status]');
      if (!button) return;
      button.hidden = false;
      this.on(button, 'click', async () => {
        button.disabled = true;
        status.textContent = 'Loading 3D view…';
        try {
          if (!this.isConnected) return;
          let model = this.querySelector('model-viewer');
          if (!model) {
            const fragment = this.querySelector('template').content.cloneNode(true);
            model = fragment.querySelector('model-viewer');
            if (!model) { this.failed(); return; }
            this.on(model, 'load', () => {
              this.querySelector('.product-model-poster')?.setAttribute('hidden', '');
              status.textContent = 'Drag to rotate · scroll or pinch to zoom';
              button.hidden = true;
              clearTimeout(this.modelTimeout);
            });
            this.on(model, 'error', () => this.failed());
            this.prepend(fragment);
          }
          // Shopify's model_viewer_tag loads its own library when the element
          // is connected. Wait for that library instead of importing a second one.
          await waitForModelViewer();
          if (!this.isConnected) return;
          this.modelTimeout = setTimeout(() => {
            if (model.loaded) return;
            this.failed();
          }, 30000);
          this.on(document, 'visibilitychange', () => { if (document.hidden) model.removeAttribute('auto-rotate'); });
        } catch { this.failed(); }
      });
    }
    failed() {
      clearTimeout(this.modelTimeout);
      this.querySelector('model-viewer')?.remove();
      const button = this.querySelector('[data-load-model]');
      button.disabled = false; button.hidden = false; button.textContent = 'RETRY 3D VIEW';
      this.querySelector('[data-model-status]').textContent = '3D view is unavailable. Product photos are still available.';
      this.querySelector('.product-model-poster')?.removeAttribute('hidden');
      this.querySelector('[data-model-fallback]')?.removeAttribute('hidden');
    }
    disconnectedCallback() { super.disconnectedCallback(); clearTimeout(this.modelTimeout); }
  }

  class ProductOptions extends ArchiveElement {
    mount() {
      const select = this.querySelector('[data-variant-select]');
      if (!select) return;
      const pills = this.querySelector('[data-variant-pills]');
      const nativeVariant = this.querySelector('[data-native-variant]');
      // Restore the original XArchive pill interface while keeping the native
      // Shopify select hidden in the visual layer and available for form submit.
      if (pills) pills.hidden = false;
      if (nativeVariant && select.options.length > 1) nativeVariant.hidden = true;
      const update = (variantId = select.value) => {
        if (variantId) select.value = variantId;
        const option = select.selectedOptions[0];
        if (!option) return;
        pills?.querySelectorAll('input[type="radio"]').forEach(input => {
          input.checked = input.value === String(select.value);
        });
        const available = option.dataset.available === 'true';
        this.querySelector('[data-variant-price]').textContent = option.dataset.price;
        const compare = this.querySelector('[data-variant-compare]');
        compare.hidden = option.dataset.sale !== 'true';
        compare.textContent = option.dataset.compare;
        this.querySelector('[data-variant-stock]').textContent = available ? 'AVAILABLE' : 'SOLD OUT';
        const submit = this.querySelector('[data-product-submit]');
        submit.disabled = !available; submit.textContent = available ? 'ADD TO BAG' : 'SOLD OUT';
        const accelerated = this.querySelector('.shopify-payment-button');
        if (accelerated) accelerated.hidden = !available;
        this.querySelectorAll('input[type="radio"]').forEach(r => r.checked = r.value === select.value);
        if (!window.Shopify?.designMode) {
          const url = new URL(location.href);
          url.searchParams.set('variant', select.value);
          history.replaceState(history.state, '', url);
        }
        this.closest('[data-product-root]')?.dispatchEvent(new CustomEvent('variant:media', { detail: option.dataset.mediaId }));
      };
      this.on(select, 'change', update);
      this.on(pills, 'change', e => {
        if (e.target.matches('input[type="radio"]')) update(e.target.value);
      });
      this.on(pills, 'click', e => {
        const pill = e.target.closest('[data-variant-id]');
        if (pill) update(pill.dataset.variantId);
      });
      this.querySelectorAll('[data-quantity-step]').forEach(button => {
        button.hidden = false;
        this.on(button, 'click', () => {
          const input = this.querySelector('[name="quantity"]');
          if (Number(button.dataset.quantityStep) > 0) input.stepUp(); else input.stepDown();
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
      // Preserve deep-linked variant selection without a fake initial change.
      const selected = select.selectedOptions[0];
      if (selected?.dataset.mediaId) this.closest('[data-product-root]')?.dispatchEvent(new CustomEvent('variant:media', { detail: selected.dataset.mediaId }));
    }
  }

  class ArchivePreloader extends ArchiveElement {
    mount() {
      this.on(this.querySelector('[data-loader-skip]'), 'click', () => this.close());
      this.on(this, 'keydown', e => {
        if (e.key === 'Escape') this.close();
        if (e.key === 'Tab') { e.preventDefault(); this.querySelector('[data-loader-skip]').focus(); }
      });
      if (window.Shopify?.designMode) {
        this.on(document, 'shopify:section:select', e => { if (e.detail.sectionId === this.dataset.sectionId) this.open(); });
        return;
      }
      let seen = false;
      try { seen = sessionStorage.getItem('xa-preloader-seen') === '1'; } catch {}
      if (seen && this.dataset.repeat === 'session') return;
      this.open();
    }
    async open() {
      if (!this.hidden) return;
      this.hidden = false; this.classList.remove('is-hidden');
      this.previousFocus = document.activeElement;
      this.inertElements = [...document.body.children].filter(e => !e.contains(this) && !e.inert && !['SCRIPT','STYLE'].includes(e.tagName));
      this.inertElements.forEach(e => { e.inert = true; });
      this.querySelector('[data-loader-skip]').focus({ preventScroll: true });
      this.started = performance.now();
      const finish = () => {
        const remaining = reduced.matches ? 0 : Math.max(0, Number(this.dataset.duration || 1) * 1000 - (performance.now() - this.started));
        this.finishTimer = setTimeout(() => this.close(), remaining);
      };
      if (document.readyState === 'complete') finish();
      else this.on(window, 'load', finish, { once: true });
      this.maxTimer = setTimeout(() => this.close(), 6500);
      const video = this.querySelector('video');
      if (video && !reduced.matches) video.play().catch(() => {});
      const template = this.querySelector('[data-preloader-model]');
      if (template && !this.querySelector('model-viewer')) {
        try {
          await loadModelLibrary();
          if (this.hidden || !this.isConnected || this.classList.contains('is-hidden')) return;
          const fragment = template.content.cloneNode(true);
          if (!reduced.matches) fragment.querySelector('model-viewer').setAttribute('auto-rotate', '');
          template.parentElement.prepend(fragment);
        } catch { /* The wordmark remains visible if 3D cannot load. */ }
      }
    }
    close() {
      clearTimeout(this.finishTimer); clearTimeout(this.maxTimer);
      this.classList.add('is-hidden');
      this.querySelectorAll('video').forEach(v => v.pause());
      this.querySelector('model-viewer')?.removeAttribute('auto-rotate');
      this.inertElements?.forEach(e => { e.inert = false; });
      this.inertElements = [];
      try { sessionStorage.setItem('xa-preloader-seen', '1'); } catch {}
      this.hideTimer = setTimeout(() => { this.hidden = true; }, reduced.matches ? 0 : 700);
      const introControl = document.querySelector('[data-intro-gate]:not([hidden]) [data-intro-enter]');
      if (introControl) introControl.focus({ preventScroll: true });
      else if (this.previousFocus?.isConnected) this.previousFocus.focus({ preventScroll: true });
      document.dispatchEvent(new CustomEvent('xa:preloader-closed'));
    }
    disconnectedCallback() {
      super.disconnectedCallback();
      clearTimeout(this.finishTimer); clearTimeout(this.maxTimer); clearTimeout(this.hideTimer);
      this.inertElements?.forEach(e => { e.inert = false; });
    }
  }

  class ArchiveClock extends ArchiveElement {
    mount() {
      const tick = () => {
        const date = new Date();
        let timezone = this.dataset.timezone || 'UTC';
        const time = this.querySelector('[data-clock-time]');
        const day = this.querySelector('[data-clock-date]');
        try {
          const options = { timeZone: timezone };
          if (time) time.textContent = date.toLocaleTimeString('en-GB', { ...options, hour12: false });
          if (day) day.textContent = date.toLocaleDateString('en-GB', { ...options, day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase();
        } catch {
          if (time) time.textContent = date.toISOString().slice(11, 19);
          if (day) day.textContent = date.toISOString().slice(0, 10);
          this.querySelector('[data-clock-zone]').textContent = 'UTC';
        }
        time?.setAttribute('datetime', date.toISOString());
      };
      tick(); this.timer = setInterval(tick, 1000);
    }
  }
  for (const [name, component] of Object.entries({
    'x-product-card': ProductCard, 'x-product-gallery': ProductGallery, 'x-spin-viewer': SpinViewer,
    'x-model-stage': ModelStage, 'x-product-options': ProductOptions, 'x-archive-clock': ArchiveClock, 'x-archive-preloader': ArchivePreloader
  })) if (!customElements.get(name)) customElements.define(name, component);
})();
